<script setup>
import { ref, computed, onMounted, defineAsyncComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
/***/
import { useApplicationStore } from '@stores/application';
/***/
import { useForm } from '@compositions/Form';
import { useInitAuthPage } from '@compositions/Auth';
import DeclineButton from "../../Partial/Admin/Users/RequestedHolidays/DeclineButton";

const Loader = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/UI/Loader'));
const FormGroup = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/UI/Form/FormGroup'));
const InputPassword = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/UI/Form/InputPassword'));
const LegalFiles = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Auth/LegalFiles'));
const Icon = defineAsyncComponent(() => import(/* webpackChunkName: "components" */'@components/UI/Icon'));
const AuthContentImage = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Auth/ContentImage'));
const RightImage = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Auth/RightImage'));

// Store
const applicationStore = useApplicationStore();

const { t, locale } = useI18n();
const router = useRouter();
const route = useRoute();

const currentApplication = computed(() => applicationStore.getCurrent);

const token = route.query.token;

const formData = {};

const passwordChanged = ref(false);

const errorMessage = ref(null);

const defaultErrorMessage = t('auth.resetPassword.form.genericError');
const genericErrorMessage = computed(() => {
    if(errorMessage.value === null) {
        return defaultErrorMessage;
    }
    return errorMessage.value;
});

//
const onSuccess = ((response) => {
    router.push({
        name: 'auth.login',
        query: {
            email: formData.email,
            'from-reset-password': 1
        }
    });
});

// Composition pour le formulaire
const {
    requesting,
    withGenericError,
    errors,
    submit
} = useForm({
    requestPath: '/auth/reset-password/change',
    onSuccess
});

// Redirige l'utilisateur s'il est déjà connecté
const { authPreparing, redirectIfLogged } = useInitAuthPage({});

const submitRequest = ((event) => {
    if(requesting.value) {
        return;
    }

    passwordChanged.value = false;
    errorMessage.value = null;

    submit(event).catch((response) => {
        if(response && response.resetPasswordMessage) {
            errorMessage.value = response.resetPasswordMessage;
        } else {
            errorMessage.value = defaultErrorMessage;
        }

    });
});

onMounted(() => {
    document.title = t('page.auth.resetPassword.title') + ' - ' + currentApplication.value.name;
});

</script>

<template>
    <loader v-if="authPreparing"></loader>

    <div class="auth-container" v-else>
        <auth-content-image></auth-content-image>
        <div class="auth_content">

            <div class="auth_title">
                <h1 class="first-title">{{ $t('auth.resetPassword.form.title') }}</h1>

                <form method="post" @submit.prevent="submitRequest" class="form-auth form-auth-reset-password">
                <p v-if="withGenericError" class="generic-message generic-message-error">
                    {{ genericErrorMessage}}
                </p>
                <form-group
                    :label="$t('auth.resetPassword.form.fields.email.label')"
                    input-id="reset-password-email"
                    :error="errors.email"
                    :with-icons="true"
                >
                    <icon name="envelope"></icon>
                    <input type="email" name="email" id="reset-password-email" v-model="formData.email" autocomplete="off" required="required" />
                </form-group>

                <form-group
                    :label="$t('auth.resetPassword.form.fields.password.label')"
                    input-id="reset-password-password"
                    :error="errors.password"
                    :with-icons="true"
                >
                    <input-password name="password" id="reset-password-password" :required="true" :autocomplete="false"></input-password>
                </form-group>

                <form-group
                    :label="$t('auth.resetPassword.form.fields.passwordConfirmation.label')"
                    input-id="reset-password-password-confirmation"
                    :error="errors.password_confirmation"
                    :with-icons="true"
                >
                    <input-password name="password_confirmation" id="reset-password-password-confirmation" :required="true" :autocomplete="false"></input-password>
                </form-group>

                <input type="hidden" v-model="token" name="token" />

                <div class="form-group">
                    <button type="submit" class="button button-primary button-loader" :class="{ loading: requesting }">
                        <span>{{ $t('auth.resetPassword.form.fields.submit.label') }}</span>
                    </button>
                </div>


            </form>
            </div>
        </div>
        <right-image></right-image>
        <div class="auth_footer">
            <legal-files></legal-files>
        </div>

    </div>

</template>
